import React from 'react';
import './GameOverScreen.css';
import { tCustomer } from '../data/Customers';

export type tGameStats = {
  companyName: string;
  totalSprints: number;
  totalStoriesReleased: number;
  totalEpicsCompleted: number;
  signedUpCustomers: number;
  bankBalance: number;
  employeesFired: number;
  mostStoryPointsInSprint: number;
  fewestStoryPointsInSprint: number;
  releaseVersion: number;
};

interface GameOverScreenProps {
  stats: tGameStats;
  customers: tCustomer[];
  onRestart?: () => void;
}

export default function GameOverScreen(props: GameOverScreenProps) {
  // Calculate score based on requirements
  const calculateScore = (): number => {
    const stats = props.stats;
    let score = 0;
    
    // 1. Money in the bank x 10
    score += stats.bankBalance * 10;
    
    // 2. Then divide by the number of sprints
    score = score / stats.totalSprints;
    
    // 3. Then add the release number multiplied by 10
    score += Math.floor(stats.releaseVersion) * 10;
    
    // 4. Add 1 point for each story released
    score += stats.totalStoriesReleased;
    
    // 5. Deduct 10 points for each time an employee was fired
    score -= stats.employeesFired * 10;
    
    // 6. Add the most story points completed in one sprint
    score += stats.mostStoryPointsInSprint;
    
    // 7. Add the fewest story points completed in one sprint
    score += stats.fewestStoryPointsInSprint;
    
    // 8. Add 10 points for each customer venue that is signed up
    score += stats.signedUpCustomers * 10;
    
    return Math.round(score);
  };

  const score = calculateScore();

  const handleShare = () => {
    const shareText = `I just completed a sprint planning simulation at ${props.stats.companyName}!\n\nScore: ${score}\nSprints: ${props.stats.totalSprints}\nStories Released: ${props.stats.totalStoriesReleased}\nEpics Completed: ${props.stats.totalEpicsCompleted}\nCustomers: ${props.stats.signedUpCustomers}\n\nCan you beat my score?`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Sprint Planning Simulator - Game Over',
        text: shareText,
      }).catch((error) => console.log('Error sharing:', error));
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(shareText).then(() => {
        alert('Score copied to clipboard!');
      }).catch((error) => {
        console.log('Error copying to clipboard:', error);
        alert('Unable to share. Please try again.');
      });
    }
  };

  return (
    <div className="game-over-overlay">
      <div className="game-over-modal">
        <h1>🎮 Game Over 🎮</h1>
        
        <div className="company-header">
          <h2>🏢 {props.stats.companyName}</h2>
        </div>

        <div className="score-section">
          <h2>Overall Score</h2>
          <div className="score-display">{score}</div>
        </div>

        <div className="stats-section">
          <h3>Game Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Total Sprints</span>
              <span className="stat-value">{props.stats.totalSprints}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Stories Released</span>
              <span className="stat-value">{props.stats.totalStoriesReleased}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Epics Completed</span>
              <span className="stat-value">{props.stats.totalEpicsCompleted}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Signed Up Customers</span>
              <span className="stat-value">{props.stats.signedUpCustomers}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Bank Balance</span>
              <span className={`stat-value ${props.stats.bankBalance < 0 ? 'negative' : 'positive'}`}>
                £{props.stats.bankBalance}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Employees Fired</span>
              <span className="stat-value">{props.stats.employeesFired}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Most Story Points (Sprint)</span>
              <span className="stat-value">{props.stats.mostStoryPointsInSprint}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Fewest Story Points (Sprint)</span>
              <span className="stat-value">{props.stats.fewestStoryPointsInSprint}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Final Release Version</span>
              <span className="stat-value">{props.stats.releaseVersion.toFixed(1)}</span>
            </div>
          </div>
        </div>

        <div className="customer-section">
          <h3>Active Customers</h3>
          <div className="customer-list">
            {props.customers.filter(c => c.isCustomer).length > 0 ? (
              props.customers.filter(c => c.isCustomer).map(customer => (
                <div key={customer.id} className="customer-item">
                  <span className="customer-icon">{customer.icon}</span>
                  <span className="customer-name">{customer.name}</span>
                  <span className="customer-sites">({customer.numberOfSites} {customer.numberOfSites === 1 ? 'site' : 'sites'})</span>
                </div>
              ))
            ) : (
              <p className="no-customers">No customers signed up</p>
            )}
          </div>
        </div>

        <div className="button-section">
          <button className="share-button" onClick={handleShare}>
            📤 Share Score
          </button>
          {props.onRestart && (
            <button className="restart-button" onClick={props.onRestart}>
              🔄 Play Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
